import React, { useState, type FC } from 'react';

import {
  Form,
  Input,
  Button,
  Typography,
  toast,
  useDeviceType,
} from 'pixel-react';
import { useNavigate } from 'react-router-dom';

import { useForgotPasswordMutation } from '@/store/forgotPasswordRequest/forgotPasswordSlice';
import type {
  FormValues,
  FormErrors,
  FormHelperTexts,
} from '@/types/forgotPassword';
import type { SignUpResponse } from '@/types/signUp';

import styles from './ForgotPassword.module.scss';
import { SignUpWrapper } from '../SignUpWrapper';

export const ForgotPassword: FC = () => {
  const clientName = import.meta.env.VITE_CLIENT_DETAIL?.name;
  const { isMobile } = useDeviceType();
  const initialFormState: FormValues = {
    email: '',
  };
  const [formData, setFormData] = useState<FormValues>(initialFormState);
  const [formErrors, setFormErrors] = useState<FormErrors>({
    email: false,
  });
  const [helperTexts, setHelperTexts] = useState<FormHelperTexts>({});

  const [isFormTouched, setIsFormTouched] = useState(false);
  const navigate = useNavigate();
  const validateField = (name: keyof FormValues, value: string) => {
    let hasError = false;
    let errorMessage = '';

    switch (name) {
      case 'email':
        hasError =
          value.trim() === '' ||
          !/^[a-zA-Z0-9]+(?:[._-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+(?:[._-][a-zA-Z0-9]+)+$/.test(
            value,
          );
        errorMessage =
          value.trim() === '' ? 'Email is required' : 'Invalid email address';
        break;
    }

    setFormErrors(prev => ({ ...prev, [name]: hasError }));
    setHelperTexts(prev => ({
      ...prev,
      [name]: hasError ? errorMessage : undefined,
    }));

    return hasError;
  };

  const handleChange =
    (name: keyof FormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFormData(prev => ({ ...prev, [name]: value }));
      validateField(name, value);
      setIsFormTouched(true);
    };
  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();
  const handleSubmit = async () => {
    // Reset error states before validating
    setFormErrors({
      email: false,
    });

    setHelperTexts({});

    // Validate all fields and collect errors
    const errors: FormErrors = {
      email: validateField('email', formData.email),
    };
    const hasErrors = Object.values(errors).some(error => error);

    if (hasErrors) {
      setIsFormTouched(true);
    }

    // Update state with any validation errors
    setFormErrors(errors);

    // If there are no errors in the form fields and captcha is verified, proceed with submission
    if (!Object.values(errors).some(error => error)) {
      try {
        const payLoad = { emailId: formData.email };
        const response = (await forgotPassword(
          payLoad,
        ).unwrap()) as SignUpResponse;

        if (response.responseCode === 200) {
          navigate(`/reset-link/${window.btoa(formData.email)}`);
        } else if ([400, 404].includes(response.responseCode)) {
          setFormErrors(prev => ({ ...prev, email: true }));
          setHelperTexts(prev => ({
            ...prev,
            email: `${response.message}`,
          }));

          //response.message.includes('does not exist')
          if (!/does(?: not|n't) exist/i.test(response.message)) {
            navigate('/user-activation?email=' + formData.email);
          }
        } else if (response.responseCode === 429) {
          toast.info(
            response?.message || 'Too many requests. Please try again later.',
          );
        } else {
          setFormErrors(prev => ({ ...prev, email: false }));
          setHelperTexts(prev => ({ ...prev, email: undefined }));
        }
      } catch (error) {
        console.error('Login failed. Please check your credentials.', error);
      }
    }
  };

  const handleBlur = (name: keyof FormValues) => {
    validateField(name, formData[name]);
  };
  const hasErrors = () => {
    const hasFieldErrors = Object.values(formErrors).some(error => error);

    return hasFieldErrors;
  };
  return (
    <SignUpWrapper
      content_header={
        <>
          {' '}
          <Typography fontSize="24px" fontWeight="bold">
            {clientName} AI - Better at being the best
          </Typography>{' '}
        </>
      }
      sub_content={`Using ${clientName}, our customers reap 10x faster scripting, 74% reduction in cost & 54% reduction in maintenance efforts, all at their fingertips.`}
      footer_content={`${clientName}: High Speed Testing, Simplified!`}
      copyright_content={`Copyright © ${clientName} Pvt Ltd. All Rights Reserved`}
      className={styles.forgotPassword_form_container}
    >
      <section className={styles.form_container}>
        <aside className={styles.forgot_password_form}>
          <section className={styles.forgetPassword_form_header}>
            <Typography fontSize="32px" className={styles['header_text']}>
              Forgot Password
            </Typography>
            <Typography
              fontSize="16px"
              textAlign="center"
              className={styles['sub_header_text']}
            >
              Please provide the email address that you used when you{' '}
              <br />{' '}
            </Typography>

            <Typography
              fontSize="16px"
              textAlign="center"
              className={styles['sub_count_header_text']}
            >
              {' '}
              signed up for your account
            </Typography>
          </section>
          <div>
            <Form<FormValues> onSubmit={handleSubmit} mode="onChange">
              {() => (
                <div>
                  <section className={`${styles.name_input} `}>
                    <Input
                      name="email"
                      label="Email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={handleChange('email')}
                      onBlur={() => handleBlur('email')}
                      error={formErrors.email}
                      helperText={helperTexts.email}
                      variant="primary"
                      type="email"
                      autoComplete="off"
                      required
                    />
                  </section>
                  <Typography
                    fontSize="16px"
                    textAlign="center"
                    className={styles['notified_text']}
                  >
                    We will send you an email that will allow you to reset your{' '}
                    <br />
                    <Typography
                      fontSize="16px"
                      className={styles['notified_text']}
                      textAlign="center"
                    >
                      password
                    </Typography>
                  </Typography>

                  <div className={styles.footerBtn_container}>
                    <div className={styles.backward_footer_btn}>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          navigate('/signin');
                        }}
                        buttonWidth={isMobile ? '100%' : '245.5px'}
                      >
                        <Typography fontSize="16px" fontWeight="bold">
                          Back to Sign In
                        </Typography>
                      </Button>
                    </div>
                    <div className={styles.forgot_password_footer_btn}>
                      <Button
                        disabled={!isFormTouched || hasErrors() || isLoading}
                        type="submit"
                        variant="primary"
                        buttonWidth={isMobile ? '100%' : '245.5px'}
                      >
                        <Typography
                          fontSize="16px"
                          className={styles['register_btn']}
                        >
                          Send reset link to email
                        </Typography>
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </Form>
          </div>
        </aside>
      </section>
    </SignUpWrapper>
  );
};
